import { AxiosResponse } from 'axios';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AssessmentSessionProcessTypeId } from '../../services/exams/assessmentSession/assessments.types';
import { approveExamTaskGroupSolutionScore } from '../../services/scoringModel/scoringModel.service';
import {
  ApproveExamTaskGroupSolutionScoreResponse,
  SendBackSampleResponse,
} from '../../services/scoringModel/scoringModel.types';
import { getProcessLabel } from '../../utils/assessmentSessionProcess';
import { getSampleAssessmentUrl } from '../../utils/routerUrls';
import { ButtonOne } from '../ButtonOne/ButtonOne.component';
import { Prompt, SubText, promptType } from '../Prompt/Prompt.component';
import TooltipOne from '../TooltipOne/TooltipOne.component';
import { useStyles } from './ConfirmDeny.styles';
import { useLayoutContext } from '../../Context/Layout.context';
import { getExamTaskWithoutRequiredCommentSubtext } from '../Assessment/Footer/Helpers.component';
interface ConfirmDeny {
  id: string;
  progress: number;
  sampleAssessmentSelectedProcess?: AssessmentSessionProcessTypeId | null;
  assessmentSessionId: string;
  onConfirmCompletedSuccesfully?: () => void;
}

export const ConfirmDeny: React.FC<ConfirmDeny> = ({
  id,
  progress,
  sampleAssessmentSelectedProcess,
  assessmentSessionId,
  onConfirmCompletedSuccesfully,
}) => {
  const styles = useStyles();
  const history = useHistory();
  const { openAlert } = useLayoutContext();
  const [prompt, setPrompt] = useState<{
    isOpen: boolean;
    type: promptType;
    mainText: string;
    promptText: string;
    subText?: SubText[];
  }>({
    isOpen: false,
    type: 'success',
    mainText: '',
    promptText: '',
  });
  const [enhancedText, setEnhancedText] = useState<JSX.Element>(<div></div>);
  const isDisabled =
    sampleAssessmentSelectedProcess === undefined || sampleAssessmentSelectedProcess === null || progress !== 100;

  const promptText = (confirm?: boolean, success?: boolean): string => {
    if (confirm === true) {
      if (success === true) {
        return `RGZ: ${id} zostało potwierdzone i pomyślnie przypisane do ${
          getProcessLabel(sampleAssessmentSelectedProcess ?? undefined).toProcess
        }.`;
      } else {
        return `Potwierdzenie i przypisanie RGZ: ${id} do procesu nie powiodło się. Spróbuj ponownie później.`;
      }
    }

    if (confirm !== undefined && success !== undefined) {
      return `${confirm ? 'Potwierdzenie ' : 'Odrzucenie '} RGZ:${id} ${
        success ? 'zakończone sukcesem.' : 'nie powiodło się. Spróbuj ponownie później.'
      }`;
    } else {
      return `Odrzucone RGZ trafi do puli rozwiązań nieocenionych.`;
    }
  };

  const handleConfirm = () => {
    if (!isDisabled) {
      approveExamTaskGroupSolutionScore(id, true)
        .then((res: AxiosResponse<SendBackSampleResponse>) => {
          if (res.data.result === ApproveExamTaskGroupSolutionScoreResponse.Success) {
            setPrompt({ isOpen: true, type: 'success', promptText: '', mainText: promptText(true, true) });
            setEnhancedText(<div></div>);
          } else if (res.data.result === ApproveExamTaskGroupSolutionScoreResponse.CriteriaCommentRequired) {
            const subText = getExamTaskWithoutRequiredCommentSubtext(res.data.examTaskWithoutRequiredComment);
            setEnhancedText(subText);
            setPrompt({
              isOpen: true,
              type: 'error',
              mainText: 'Nie można zatwierdzić RGZ, ponieważ nie został wprowadzony komentarz do:',
              promptText: '',
            });
          } else {
            setPrompt({ isOpen: true, type: 'error', promptText: '', mainText: promptText(true, false) });
            setEnhancedText(<div></div>);
          }
        })
        .catch(() => {
          if (!navigator.onLine) {
            openAlert('error', 'W wyniku braku połączenia z internetem zapisywanie danych nie powiodło się.');
            setPrompt((prev) => ({ ...prev, isOpen: false }));
          } else {
            setPrompt({ isOpen: true, type: 'error', promptText: '', mainText: promptText(true, false) });
            setEnhancedText(<div></div>);
          }
        });
    }
  };

  const confirmRejection = () => {
    setEnhancedText(<div></div>);
    approveExamTaskGroupSolutionScore(id, false)
      .then((res: AxiosResponse<SendBackSampleResponse>) => {
        res.data.result === ApproveExamTaskGroupSolutionScoreResponse.Success
          ? setPrompt({ isOpen: true, type: 'success', promptText: '', mainText: promptText(false, true) })
          : setPrompt({ isOpen: true, type: 'error', promptText: '', mainText: promptText(false, false) });
      })
      .catch(() => {
        if (!navigator.onLine) {
          openAlert('error', 'W wyniku braku połączenia z internetem zapisywanie danych nie powiodło się.');
          setPrompt((prev) => ({ ...prev, isOpen: false }));
        } else setPrompt({ isOpen: true, type: 'error', promptText: '', mainText: promptText(false, false) });
      });
  };

  const successConfirmHandle = () => {
    onConfirmCompletedSuccesfully && onConfirmCompletedSuccesfully();
    setPrompt((prev) => ({ ...prev, isOpen: false }));
  };

  const handleOnConfirm = () => {
    switch (prompt.type) {
      case 'confirmation':
        confirmRejection();
        break;
      case 'error':
        setPrompt((prev) => ({ ...prev, isOpen: false }));
        break;
      default:
        successConfirmHandle();
    }
  };

  const handleDeny = () => {
    setPrompt({
      isOpen: true,
      type: 'confirmation',
      mainText: `Czy na pewno chcesz odrzucić RGZ:${id}?`,
      promptText: promptText(),
    });
    setEnhancedText(<div></div>);
  };

  const handleOnCancel = (): void | undefined => {
    switch (prompt.type) {
      case 'confirmation':
        setPrompt((prev) => ({ ...prev, isOpen: false }));
        break;
      case 'success':
      case 'error':
        history.push(getSampleAssessmentUrl(assessmentSessionId));
        break;
    }
  };

  const button = (): JSX.Element => {
    return (
      <ButtonOne
        aria-label="Zatwierdź"
        onClick={handleConfirm}
        disabled={isDisabled}
        variant="outlined"
        className={styles.deny}
      >
        Zatwierdź
      </ButtonOne>
    );
  };

  return (
    <>
      <span className={styles.containerButtons}>
        <ButtonOne onClick={handleDeny} variant="outlined" className={styles.confirm} aria-label="Odrzuć">
          Odrzuć
        </ButtonOne>
        {isDisabled ? (
          <TooltipOne title={'Wybierz proces, by móc zatwierdzić RGZ'} placement="top">
            <span>{button()}</span>
          </TooltipOne>
        ) : (
          button()
        )}
      </span>
      <Prompt
        type={prompt.type}
        open={prompt.isOpen}
        mainText={prompt.mainText}
        subTexts={
          !prompt.subText
            ? [
                {
                  text: prompt.promptText,
                },
              ]
            : prompt.subText
        }
        enhancedText={enhancedText}
        isBold={true}
        mainActionButtonText={
          prompt.type === 'confirmation'
            ? 'TAK'
            : prompt.type === 'success' || !prompt.promptText
            ? 'POWRÓT DO RGZ'
            : 'POWRÓT'
        }
        onCancel={prompt.type !== 'error' || prompt.subText ? handleOnCancel : undefined}
        onConfirm={handleOnConfirm}
        cancelButtonText={prompt.type === 'success' || !prompt.promptText ? 'POWRÓT DO LISTY' : undefined}
      />
    </>
  );
};
